// src/admin/pages/support/users/SupportUserOverview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getSupportUserDetail } from "../../../../api/adminSupportApi";

function timeAgo(date) {
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms)) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} month${mon === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mon / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function fmtDateTime(x) {
  if (!x) return { pretty: "—", rel: "" };
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return { pretty: String(x), rel: "" };

  const pretty = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);

  return { pretty, rel: timeAgo(d) };
}

function copyText(x) {
  const v = String(x || "");
  if (!v) return;
  try {
    navigator.clipboard?.writeText(v);
  } catch {}
}

function Pill({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return <span className={`inline-flex items-center rounded-xl border px-3 py-1.5 text-[12px] font-semibold ${cls}`}>{children}</span>;
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
        {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, hint, action }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-2">
      <div className="col-span-12 md:col-span-4 text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="col-span-12 md:col-span-8 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 break-words">{value ?? "—"}</div>
          {hint ? <div className="mt-0.5 text-[12px] font-medium text-slate-500">{hint}</div> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900 tabular-nums">{value ?? "—"}</div>
      {hint ? <div className="mt-1 text-[12px] font-medium text-slate-500">{hint}</div> : null}
    </div>
  );
}

export default function SupportUserOverview() {
  const { entityId } = useParams();

  const userId = useMemo(() => {
    if (!entityId) return null;
    if (!/^\d+$/.test(String(entityId))) return null;
    return String(entityId);
  }, [entityId]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [u, setU] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr("");
        setLoading(true);

        if (!userId) {
          setErr("Invalid user id in URL");
          return;
        }

        const data = await getSupportUserDetail(userId);
        if (!alive) return;
        setU(data);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load user");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-600">Loading overview…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
        <div className="text-sm font-semibold text-rose-700">{err}</div>
      </div>
    );
  }

  if (!u) return null;

  const created = fmtDateTime(u.createdAt);
  const updated = fmtDateTime(u.updatedAt);
  const lastAct = fmtDateTime(u.lastActivityAt);
  const lastReq = fmtDateTime(u.lastRequestAt);
  const lastBook = fmtDateTime(u.lastBookingAt);
  const blockedAt = fmtDateTime(u.blockedAt);

  const statusLabel = u.enabled === true ? "Active" : u.enabled === false ? "Blocked" : "—";
  const statusTone = u.enabled === true ? "good" : u.enabled === false ? "bad" : "neutral";

  const hasLatestRequest = !!u?.lastRequestId || !!u?.lastRequestStatus || !!u?.lastRequestPaymentStatus;
  const hasLatestBooking = !!u?.lastBookingId || !!u?.lastBookingStatus || !!u?.lastBookingPaymentStatus;

  return (
    <div className="min-w-0 space-y-5">
      {/* Top summary */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="text-[12px] font-semibold uppercase tracking-wider text-slate-500">
                User overview
              </div>
              <div className="mt-1 truncate text-2xl font-extrabold tracking-tight text-slate-900">
                {u.userName || "—"}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Pill tone={statusTone}>Status: {statusLabel}</Pill>
                <Pill>Role: {u.role || "—"}</Pill>
                <Pill>Phone: {u.phoneNumber || "—"}</Pill>
              </div>

              <div className="mt-3 text-[13px] font-medium text-slate-500">
                Created <span className="font-semibold text-slate-900">{created.pretty}</span>
                {created.rel ? <span className="text-slate-400"> ({created.rel})</span> : null}
                <span className="text-slate-300"> • </span>
                Updated <span className="font-semibold text-slate-900">{updated.pretty}</span>
                {updated.rel ? <span className="text-slate-400"> ({updated.rel})</span> : null}
              </div>
            </div>

            {/* Contact actions */}
            <div className="grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Phone</div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="text-sm font-extrabold text-slate-900 tabular-nums">{u.phoneNumber || "—"}</div>
                  <button
                    onClick={() => copyText(u.phoneNumber)}
                    className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email</div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 truncate text-sm font-extrabold text-slate-900">{u.email || "—"}</div>
                  <button
                    onClick={() => copyText(u.email)}
                    className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-4">
            <Metric label="Total bookings" value={u.totalBookings ?? 0} hint={`Cancelled: ${u.cancelledBookings ?? 0}`} />
            <Metric label="Total requests" value={u.totalRequests ?? 0} hint={`Active: ${u.activeRequests ?? 0}`} />
            <Metric label="Last activity" value={lastAct.pretty} hint={lastAct.rel ? `Seen ${lastAct.rel}` : ""} />
            <Metric label="Blocked at" value={blockedAt.pretty} hint={u.enabled === false ? (blockedAt.rel ? blockedAt.rel : "") : "Not blocked"} />
          </div>
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-6 space-y-5">
          <Section title="Activity" subtitle="Recent activity timestamps (admin friendly).">
            <InfoRow label="Last activity" value={lastAct.pretty} hint={lastAct.rel ? `Seen ${lastAct.rel}` : ""} />
            <InfoRow label="Last request created" value={lastReq.pretty} hint={lastReq.rel ? `Created ${lastReq.rel}` : ""} />
            <InfoRow label="Last booking created" value={lastBook.pretty} hint={lastBook.rel ? `Created ${lastBook.rel}` : ""} />
          </Section>

          <Section title="Audit" subtitle="System timestamps and admin blocking metadata.">
            <InfoRow label="Created at" value={created.pretty} hint={created.rel || ""} />
            <InfoRow label="Updated at" value={updated.pretty} hint={updated.rel || ""} />
            <InfoRow label="Blocked at" value={blockedAt.pretty} hint={blockedAt.rel || ""} />
            <InfoRow label="Blocked by admin ID" value={u.blockedByAdminId ?? "—"} />
            <InfoRow label="Block reason" value={u.blockReason ?? "—"} />
          </Section>
        </div>

        <div className="col-span-12 lg:col-span-6 space-y-5">
          <Section title="Latest pointers" subtitle="Most recent request/booking and payment status.">
            {hasLatestRequest ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Last request</div>
                <div className="mt-3 space-y-2">
                  <InfoRow label="Request ID" value={u.lastRequestId ?? "—"} />
                  <InfoRow label="Status" value={u.lastRequestStatus ?? "—"} />
                  <InfoRow label="Payment" value={u.lastRequestPaymentStatus ?? "—"} />
                </div>
              </div>
            ) : (
              <div className="text-sm font-medium text-slate-500">No request pointer available.</div>
            )}

            {hasLatestBooking ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Last booking</div>
                <div className="mt-3 space-y-2">
                  <InfoRow label="Booking ID" value={u.lastBookingId ?? "—"} />
                  <InfoRow label="Status" value={u.lastBookingStatus ?? "—"} />
                  <InfoRow label="Payment" value={u.lastBookingPaymentStatus ?? "—"} />
                </div>
              </div>
            ) : (
              <div className="mt-4 text-sm font-medium text-slate-500">No booking pointer available.</div>
            )}
          </Section>

          <Section title="Location" subtitle="Profile + live location values (if available).">
            <InfoRow label="User location (profile)" value={u.userLocation ?? "—"} />
            <InfoRow label="Current location (live)" value={u.currentLocation ?? "—"} />
            <div className="my-3 h-px bg-slate-200" />
            <InfoRow label="Latitude" value={u.latitude ?? "—"} />
            <InfoRow label="Longitude" value={u.longitude ?? "—"} />
          </Section>
        </div>
      </div>
    </div>
  );
}
